import React, { useEffect, useRef, useState } from 'react';
import { hapticTick } from '../../../_utils/haptics';

/**
 * One of the soul's dials: five stops, handled like a proper control.
 *
 * While it's held the thumb follows the finger exactly and the word (and the
 * preview) changes as it passes each stop; let go and it glides to the
 * nearest stop. A touch only takes hold once it moves sideways, so scrolling
 * the page past a dial never changes it. Arrow keys step a stop at a time.
 */
const RAIL_INSET = 12; // the thumb's radius: its centre travels between the rail's ends
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

export default function SoulDial({ id, label, words, ends, value, onChange }) {
    const stops = words.length - 1;
    const el = useRef(null);
    const [held, setHeld] = useState(null);       // 0..1 while held, else null
    const press = useRef(null);                    // { id, x, y, engaged }
    const sent = useRef(value);
    useEffect(() => { if (held == null) sent.current = value; }, [value, held]);

    const fractionAt = (clientX) => {
        const r = el.current.getBoundingClientRect();
        return clamp((clientX - r.left - RAIL_INSET) / Math.max(1, r.width - RAIL_INSET * 2), 0, 1);
    };
    const follow = (clientX) => {
        const f = fractionAt(clientX);
        setHeld(f);
        const v = Math.round(f * stops);
        if (v !== sent.current) { sent.current = v; hapticTick(); onChange(v); }
    };
    const engage = (e) => {
        press.current.engaged = true;
        try { el.current.setPointerCapture(e.pointerId); } catch (err) { /* follow without capture */ }
        follow(e.clientX);
    };
    const release = () => { press.current = null; setHeld(null); };

    const onPointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        press.current = { id: e.pointerId, x: e.clientX, y: e.clientY, engaged: false };
        if (el.current) el.current.focus({ preventScroll: true });
        // A mouse takes hold at once; a finger waits to see which way it goes.
        if (e.pointerType === 'mouse') { e.preventDefault(); engage(e); }
    };
    const onPointerMove = (e) => {
        const p = press.current;
        if (!p || p.id !== e.pointerId) return;
        if (p.engaged) { follow(e.clientX); return; }
        const dx = Math.abs(e.clientX - p.x); const dy = Math.abs(e.clientY - p.y);
        if (dx > 4 && dx > dy) engage(e);
    };
    const onPointerUp = (e) => {
        const p = press.current;
        if (!p || p.id !== e.pointerId) return;
        // A tap on the rail jumps to that stop.
        if (!p.engaged && Math.abs(e.clientX - p.x) < 8 && Math.abs(e.clientY - p.y) < 8) follow(e.clientX);
        release();
    };
    const onKeyDown = (e) => {
        const step = { ArrowLeft: -1, ArrowDown: -1, PageDown: -1, ArrowRight: 1, ArrowUp: 1, PageUp: 1 }[e.key];
        let next = null;
        if (step) next = clamp(value + step, 0, stops);
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = stops;
        if (next == null) return;
        e.preventDefault();
        if (next !== value) { hapticTick(); onChange(next); }
    };

    const pos = held != null ? held : value / stops;
    const shown = held != null ? Math.round(held * stops) : value;
    return (
        <div className="soul-dial">
            <div className="soul-dial-head">
                <span id={`${id}-label`}>{label}</span>
                <span className="soul-dial-word" aria-hidden="true">{words[shown]}</span>
            </div>
            <div
                ref={el}
                className={`soul-slider${held != null ? ' is-held' : ''}`}
                role="slider"
                tabIndex={0}
                aria-labelledby={`${id}-label`}
                aria-valuemin={0}
                aria-valuemax={stops}
                aria-valuenow={value}
                aria-valuetext={words[value]}
                style={{ '--pos': pos }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={release}
                onLostPointerCapture={() => { if (press.current && press.current.engaged) release(); }}
                onKeyDown={onKeyDown}
            >
                <span className="soul-slider-rail">
                    <span className="soul-slider-fill" />
                    {words.map((w, i) => (
                        <span key={w} className={`soul-slider-stop${i / stops <= pos + 0.001 ? ' is-past' : ''}`} style={{ left: `${(i / stops) * 100}%` }} />
                    ))}
                </span>
                <span className="soul-slider-thumb" />
            </div>
            <div className="soul-ends" aria-hidden="true"><span>{ends[0]}</span><span>{ends[1]}</span></div>
        </div>
    );
}

/** Three choices with a highlight that slides between them. */
export function SoulSegment({ id, label, hint, options, value, onChange }) {
    const onKeyDown = (e) => {
        const step = { ArrowLeft: -1, ArrowUp: -1, ArrowRight: 1, ArrowDown: 1 }[e.key];
        if (!step) return;
        e.preventDefault();
        const next = clamp(value + step, 0, options.length - 1);
        if (next !== value) { hapticTick(); onChange(next); }
        const btn = e.currentTarget.querySelector(`[data-i="${next}"]`);
        if (btn) btn.focus();
    };
    return (
        <div className="soul-dial">
            <div className="soul-dial-head"><span id={`${id}-label`}>{label}</span>{hint && <small>{hint}</small>}</div>
            <div className="soul-seg" role="radiogroup" aria-labelledby={`${id}-label`} style={{ '--i': value, '--n': options.length }} onKeyDown={onKeyDown}>
                <span className="soul-seg-pill" aria-hidden="true" />
                {options.map((o, i) => (
                    <button key={o} type="button" role="radio" data-i={i} aria-checked={value === i} tabIndex={value === i ? 0 : -1}
                        onClick={() => { if (value !== i) { hapticTick(); onChange(i); } }}>
                        {o}
                    </button>
                ))}
            </div>
        </div>
    );
}
